import { Container, Grid } from '@material-ui/core';
import * as React from 'react';
import AppBar from '@material-ui/core/AppBar';
import Toolbar from '@material-ui/core/Toolbar';
import Button from '@material-ui/core/Button';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogContentText from '@material-ui/core/DialogContentText';
import DialogTitle from '@material-ui/core/DialogTitle';
import CircularProgress from '@material-ui/core/CircularProgress';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import { useTheme } from '@material-ui/core/styles';
import { Form, FormRenderProps, FormSpy } from 'react-final-form';
import arrayMutators from 'final-form-arrays';
import { setIn } from 'final-form';
import createDecorator from 'final-form-focus';
import { useStyles } from './css';
import { ListingPreviewer } from 'components/ListingPreviewer/ListingPreviewer';
import { Breadcrumb, PageHeading } from '../PageHeading';
import { AppListingFragment } from '../../types/generated';

export type FormPageTypeEnum = 'create' | 'edit';

interface IFormPage<T> {
    title: string;
    breadcrumbs?: Breadcrumb[];
    initialValues: T;
    validationSchema: any;
    onSubmit: (values: T, form, callback) => void;
    canSubmit?: boolean;
    onCancel?: () => void;
    onDelete?: () => void;
    deleting?: boolean;
    loading: boolean;
    render: (props: FormRenderProps<T>) => React.ReactNode;
    previewComponent?: JSX.Element;
    type?: FormPageTypeEnum;
}

export const FormPage = <T extends Record<string, any>>({
    title,
    breadcrumbs,
    initialValues,
    validationSchema,
    onSubmit,
    onCancel,
    onDelete,
    deleting,
    loading,
    render,
    previewComponent,
    canSubmit = true,
}: IFormPage<T>) => {
    const classes = useStyles();

    const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false);
    const [previewDialogOpen, setPreviewDialogOpen] = React.useState(false);

    const handleCloseDeleteDialog = () => {
        setDeleteDialogOpen(false);
    };

    const theme = useTheme();
    const fullScreenDialog = useMediaQuery(theme.breakpoints.down('sm'));

    const validate = schema => {
        // eslint-disable-next-line consistent-return
        return async values => {
            if (typeof schema === 'function') {
                schema = schema();
            }

            try {
                await schema.validate(values, { abortEarly: false });
            } catch (e: any) {
                return e.inner.reduce((errors, error) => {
                    return setIn(errors, error.path, error.message);
                }, {});
            }
        };
    };
    const subscription = { submitting: true, pristine: true };
    const focusOnError = createDecorator();

    return (
        <Container maxWidth="md">
            <PageHeading title={title} breadcrumbs={breadcrumbs} />
            <Form
                initialValues={initialValues}
                validate={validationSchema ? validate(validationSchema) : undefined}
                subscription={subscription}
                decorators={[focusOnError]}
                mutators={{
                    ...arrayMutators,
                }}
                onSubmit={canSubmit ? onSubmit : () => null}
            >
                {props => (
                    <form className={classes.form} onSubmit={props.handleSubmit} noValidate>
                        {render(props)}

                        <FormSpy subscription={{ values: true }}>
                            {({ values: formValues }: { values: AppListingFragment }) => (
                                <>
                                    {previewComponent && (
                                        <ListingPreviewer
                                            listing={formValues}
                                            previewDialogOpen={previewDialogOpen}
                                            setPreviewDialogOpen={setPreviewDialogOpen}
                                        />
                                    )}
                                </>
                            )}
                        </FormSpy>

                        <div className={classes.formSpacer} />
                        <AppBar position="fixed" color="default" className={classes.formActions}>
                            <Toolbar>
                                <Container maxWidth="md" className={classes.formActionContainer}>
                                    <Grid container justify="space-between" alignItems="center">
                                        <Grid item>
                                            {onDelete && (
                                                <Button
                                                    onClick={() => setDeleteDialogOpen(true)}
                                                    disabled={deleting}
                                                    variant="contained"
                                                    color="secondary"
                                                    size="large"
                                                    className={classes.formActionsButton}
                                                >
                                                    {deleting ? (
                                                        <CircularProgress size={24} />
                                                    ) : (
                                                        <React.Fragment>Delete</React.Fragment>
                                                    )}
                                                </Button>
                                            )}

                                            <Dialog
                                                fullScreen={fullScreenDialog}
                                                open={deleteDialogOpen}
                                                onClose={handleCloseDeleteDialog}
                                                aria-labelledby="responsive-dialog-title"
                                            >
                                                <DialogTitle id="responsive-dialog-title">
                                                    {'Are you sure?'}
                                                </DialogTitle>
                                                <DialogContent>
                                                    <DialogContentText>
                                                        You are about to delete this entity. Please
                                                        confirm.
                                                    </DialogContentText>
                                                </DialogContent>
                                                <DialogActions>
                                                    <Button
                                                        autoFocus
                                                        onClick={handleCloseDeleteDialog}
                                                        color="primary"
                                                    >
                                                        Cancel
                                                    </Button>
                                                    <Button
                                                        onClick={onDelete}
                                                        color="secondary"
                                                        autoFocus
                                                    >
                                                        {deleting ? (
                                                            <CircularProgress size={24} />
                                                        ) : (
                                                            <React.Fragment>
                                                                Yes, delete
                                                            </React.Fragment>
                                                        )}
                                                    </Button>
                                                </DialogActions>
                                            </Dialog>
                                        </Grid>
                                        <Grid item>
                                            {previewComponent && (
                                                <Button
                                                    onClick={() => {
                                                        setPreviewDialogOpen(true);
                                                    }}
                                                    variant="outlined"
                                                    color="primary"
                                                    size="large"
                                                    className={classes.formActionsButton}
                                                >
                                                    Preview
                                                </Button>
                                            )}
                                            {onCancel && (
                                                <Button
                                                    onClick={onCancel}
                                                    variant="outlined"
                                                    color="primary"
                                                    size="large"
                                                    className={classes.formActionsButton}
                                                >
                                                    Cancel
                                                </Button>
                                            )}

                                            <FormSpy
                                                subscription={{
                                                    pristine: true,
                                                    submitting: true,
                                                }}
                                            >
                                                {({ pristine, submitting }) => {
                                                    return (
                                                        <Button
                                                            type="submit"
                                                            variant="contained"
                                                            color="primary"
                                                            size="large"
                                                            className={classes.formActionsButton}
                                                            disabled={
                                                                !canSubmit ||
                                                                pristine ||
                                                                loading ||
                                                                submitting
                                                            }
                                                        >
                                                            {loading ? (
                                                                <CircularProgress size={24} />
                                                            ) : (
                                                                <React.Fragment>
                                                                    Save
                                                                </React.Fragment>
                                                            )}
                                                        </Button>
                                                    );
                                                }}
                                            </FormSpy>
                                        </Grid>
                                    </Grid>
                                </Container>
                            </Toolbar>
                        </AppBar>
                    </form>
                )}
            </Form>
        </Container>
    );
};
